import { createWalletConnector } from '@cef-ai/wallet-react';
import type { ConnectedWallet, WalletConnectConfig, WalletConnector } from '@cef-ai/wallet-react';
import type { CefSigner, DelegationRequest, DelegationResult } from '@cef-ai/wallet';
import type { PlatformEnv } from '@/lib/platform-client';
import { WALLET_ORIGINS } from '@/lib/platform-client';

/** Compatibility alias: consumers still spell the env type `CereWalletEnv`. */
export type CereWalletEnv = PlatformEnv;

/**
 * Connected passkey wallet as the vault UI consumes it.
 *
 * Two auth surfaces, never mixed on one `VaultSDK` instance (its config is
 * `signer` XOR `auth`):
 *
 * - `signingSigner` (the connector's `CefSigner`) goes to
 *   `VaultSDKConfig.signer` for consent, writes and provisioning — the things
 *   a user actually clicks. Raw signatures need the PRF-derived session keys.
 * - `requestDelegation` mints the Bearer token for the DATA-PLANE READ
 *   instance. This is the one that makes a page refresh work: `resume()`
 *   restores only the bearer session, never the signing keys (documented
 *   invariant in @cef-ai/wallet — `hasKeys` is false after EVERY reload), so a
 *   direct-sign read would demand a step-up popup with no user gesture to
 *   spend and the browser would block it. `requestDelegation` needs only
 *   `isAuthenticated`, so it succeeds bearer-active with no keys, no ceremony
 *   and no popup — which is what restores parity with the old wallet's silent
 *   rehydrate.
 */
export interface CereWallet {
  signingSigner: CefSigner;
  requestDelegation: (req: DelegationRequest) => Promise<DelegationResult>;
  logout: () => Promise<void>;
  /**
   * Live read of whether the signing keys are in the iframe right now, i.e.
   * whether `signingSigner.sign()` would go through silently or open a
   * step-up popup. A METHOD, not a snapshot: warmth changes underneath us
   * (a step-up this app never mediated, a cross-tab logout), so a boolean
   * captured at connect time would be stale by the time anyone read it.
   *
   * `false` is the state after EVERY page reload — `resume()` restores the
   * bearer session, never the PRF-derived keys — so it does not mean "broken",
   * it means "a signature from here needs a gesture". Reads do not need it:
   * `requestDelegation` succeeds bearer-active.
   */
  hasKeys: () => boolean;
  /**
   * Which surface a ceremony from here would use: `'iframe'` renders in the
   * wallet's cross-origin frame behind its own Confirm button; `'popup'` means
   * a `window.open`, which the browser blocks without transient activation.
   *
   * So `'popup'` is the answer to "would a signature from here need a gesture
   * I no longer have?" — read it before starting work you cannot undo from a
   * flow whose signature lands later than the click that began it.
   *
   * A METHOD, not a snapshot, and the distinction is load-bearing: the wallet
   * learns a policy refusal only by BEING refused, which happens long after
   * `connect()` resolved. A value captured at map time would report `'iframe'`
   * forever. Pinned by connect.test.ts.
   *
   * SUPERSEDES `warmUp()`, removed in @cef-ai/wallet 3.0.0. That call existed
   * to spend the one unavoidable step-up popup at a moment we chose; with the
   * ceremony in-frame there is no popup to pre-spend. It is NOT an equivalent
   * replacement — see `lib/clients/vault/claim.ts` for what is weaker and on
   * which engines.
   */
  ceremonySurface: () => 'iframe' | 'popup';
  publicKeyHex: string;
  address: string;
  /**
   * The account's VERIFIED email, as the wallet reports it.
   *
   * SUPERSEDES the pre-S0 behaviour, which several comments in this repo used
   * to describe: the address was whatever string the user typed into vault's
   * own create-wallet form, it reached us only through `registerCereWallet`,
   * and it was `undefined` after every `connect()`/`resume()`. Now the wallet
   * origin verifies it by one-time code and stores it on the account, so it is
   * populated on all three paths and is never a string this app supplied.
   *
   * ABSENT means the wallet reported none — a pre-S0 account, or a wallet
   * deployment older than the account block. Read that as unknown, never as
   * "this user has no email".
   */
  email?: string;
  /** The account's display name, absent on the same terms as {@link email}. */
  name?: string;
}

function walletConfig(env: CereWalletEnv): WalletConnectConfig {
  return { appId: 'vault', appName: 'CEF Vault', walletOrigin: WALLET_ORIGINS[env] };
}

type ConnectorFactory = (env: CereWalletEnv) => WalletConnector;

// One connector per env (StrictMode double-invoke + reconnect must not build
// two transports). The connector owns the EmbedWallet singleton internally.
let connectorFactory: ConnectorFactory = (env) => createWalletConnector(walletConfig(env));
const connectorCache = new Map<CereWalletEnv, WalletConnector>();

function getConnector(env: CereWalletEnv): WalletConnector {
  let c = connectorCache.get(env);
  if (!c) {
    c = connectorFactory(env);
    connectorCache.set(env, c);
  }
  return c;
}

/** Test seam — wallet-react does not export its EmbedWallet hook, so override
 *  at the connector boundary. */
export function setWalletFactoriesForTests(overrides: { connector?: ConnectorFactory }): void {
  if (overrides.connector) {
    connectorFactory = overrides.connector;
    connectorCache.clear();
  }
}

export function resetWalletFactoriesForTests(): void {
  connectorFactory = (env) => createWalletConnector(walletConfig(env));
  connectorCache.clear();
}

/**
 * `connector` is threaded in rather than reached through `c` because
 * `hasKeys`/`ceremonySurface` live on `WalletConnector`, NOT on `ConnectedWallet` —
 * `ConnectedWallet` carries only the connected identity (signer, address,
 * delegation, applications, logout). There is exactly one connector per env
 * (`getConnector`), so this is the same object every caller already shares.
 */
function toCereWallet(c: ConnectedWallet, connector: WalletConnector): CereWallet {
  return {
    signingSigner: c.signer,
    // Bound like `logout` below and for the same reason — `ConnectedWallet`
    // declares it with method syntax, so a bare property reference would
    // detach it from `c`.
    requestDelegation: (req) => c.requestDelegation(req),
    // Not `logout: c.logout` — `ConnectedWallet` declares `logout()` with
    // method syntax, so a plain property reference would detach it from `c`
    // and break if the connector ever implements it as a real prototype
    // method rather than an arrow field. Wrap it so `c` stays the receiver.
    logout: () => c.logout(),
    // Wrapped, not `hasKeys: connector.hasKeys` — same detached-receiver
    // hazard as `logout` above, and doubly so here: these are declared with
    // method syntax on `WalletConnector` and the shipped implementation reads
    // private state off `this`. Wrapping also keeps them LIVE reads of the
    // connector rather than values frozen at connect time, which is the whole
    // point of `hasKeys` AND of `ceremonySurface` (a policy refusal is learned
    // by being refused, long after connect() resolved).
    hasKeys: () => connector.hasKeys(),
    ceremonySurface: () => connector.ceremonySurface(),
    publicKeyHex: c.publicKeyHex,
    address: c.address,
    ...(c.email ? { email: c.email } : {}),
    ...(c.name ? { name: c.name } : {}),
  };
}

export async function connectCereWallet(env: CereWalletEnv = 'dev'): Promise<CereWallet> {
  const connector = getConnector(env);
  return toCereWallet(await connector.connect(), connector);
}

/**
 * In-app wallet creation. Takes no email or name: the wallet origin's register
 * popup runs email → one-time code → passkey itself, because it is the only
 * surface that can verify a mailbox.
 *
 * SUPERSEDES the pre-S0 contract, which this function's own doc comment used
 * to state as "email required + optional name". That was accurate then and is
 * wrong now — `createWallet()` accepts no arguments, and a host-supplied
 * address was discarded even before it was removed.
 *
 * MUST be called inside a user gesture (it opens a popup), and can stay
 * outstanding for MINUTES while the reader fetches their code — it is no
 * longer the seconds a bare passkey ceremony took. It settles by itself if the
 * user cancels or closes the popup.
 *
 * Resolving is NOT proof of a new account: the popup offers "Sign in" when the
 * address already has a wallet. See `accountCreated` on wallet-react's
 * `ConnectedWallet` if a caller ever needs to tell the two apart — vault does
 * not, because vault infers no first-run state (see `claimVault`).
 */
export async function registerCereWallet(env: CereWalletEnv): Promise<CereWallet> {
  const connector = getConnector(env);
  return toCereWallet(await connector.createWallet(), connector);
}

/** Silent boot-time reconnect: never opens a popup. Returns null with no live
 *  session so callers fall back to the "Reconnect" CTA. */
export async function resumeCereWallet(env: CereWalletEnv = 'dev'): Promise<CereWallet | null> {
  const connector = getConnector(env);
  const c = await connector.resume();
  return c ? toCereWallet(c, connector) : null;
}
